import React, { useState, useEffect } from 'react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { getAvailableVoices, speak, stop, setEngine } from '../utils/ttsService';
import { TTS_ENGINES } from '../constants/tts';

const TTSAdminPage = () => {
    const [config, setConfig] = useState({
        engine: TTS_ENGINES.BROWSER,
        patient_voice_id: '',
        narrator_voice_id: '',
        default_rate: 1.0,
        auto_gender_select: true,
        default_narration: 'all'
    });
    const [voices, setVoices] = useState([]);
    const [availableEngines, setAvailableEngines] = useState([TTS_ENGINES.BROWSER]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [configRes, engineRes] = await Promise.all([
                    api.getTTSConfig(),
                    api.get('/api/tts/engines')
                ]);

                setConfig(configRes.data);
                if (engineRes.data && engineRes.data.engines) {
                    setAvailableEngines(engineRes.data.engines);
                }

                const voiceResponse = await api.getTTSVoices(configRes.data.engine);
                setVoices(voiceResponse.data);
            } catch (error) {
                console.error('Failed to fetch TTS admin data:', error);
                toast.error('Failed to load settings');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, []);

    const handleEngineChange = async (engine) => {
        setConfig(prev => ({ ...prev, engine, patient_voice_id: '', narrator_voice_id: '' }));
        setLoading(true);
        try {
            const voiceResponse = await api.getTTSVoices(engine);
            setVoices(voiceResponse.data);
        } catch (error) {
            console.error('Failed to fetch voices for engine:', engine);
            setVoices([]);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        setSaving(true);
        try {
            await api.updateTTSConfig(config);
            toast.success('TTS Configuration saved');
        } catch (error) {
            console.error('Failed to save TTS config:', error);
            toast.error('Failed to save settings');
        } finally {
            setSaving(false);
        }
    };

    const handleTestVoice = async (voiceId) => {
        if (!voiceId) return;
        stop();
        // Temporarily set engine for test speak
        setEngine(config.engine);

        await speak("This is a test of the selected voice for the patient simulator.", voiceId, { rate: config.default_rate });

        // Restore engine if needed (though usually we want to stay on selected)
    };

    if (loading && !saving) {
        return <div className="p-8">Loading TTS settings...</div>;
    }

    return (
        <div className="max-w-4xl mx-auto p-8">
            <h1 className="text-2xl font-bold mb-6">TTS Administration</h1>

            <div className="bg-white shadow rounded-lg p-6 space-y-8">
                {/* Engine Selection */}
                <section>
                    <h2 className="text-lg font-semibold mb-4 text-gray-700">Service Engine</h2>
                    <div className="flex gap-4">
                        {[TTS_ENGINES.BROWSER, TTS_ENGINES.EDGE, TTS_ENGINES.PIPER].map(engine => {
                            const isAvailable = availableEngines.includes(engine);
                            return (
                                <label
                                    key={engine}
                                    className={`flex-1 p-4 border rounded-lg cursor-pointer transition-colors ${config.engine === engine ? 'border-blue-500 bg-blue-50' : 'hover:bg-gray-50'} ${!isAvailable ? 'opacity-50 grayscale' : ''}`}
                                >
                                    <input
                                        type="radio"
                                        name="engine"
                                        value={engine}
                                        checked={config.engine === engine}
                                        onChange={() => isAvailable && handleEngineChange(engine)}
                                        className="mr-2"
                                        disabled={!isAvailable}
                                    />
                                    <span className="capitalize font-medium">{engine} TTS</span>
                                    {!isAvailable && <span className="ml-2 text-[10px] bg-red-100 text-red-600 px-1 rounded">Offline</span>}
                                    <p className="text-xs text-gray-500 mt-1">
                                        {engine === TTS_ENGINES.BROWSER && "Uses local device voices (Free)"}
                                        {engine === TTS_ENGINES.EDGE && "Microsoft Azure Neural voices (High Quality)"}
                                        {engine === TTS_ENGINES.PIPER && "Local Neural voices (Requires separate server)"}
                                    </p>
                                </label>
                            );
                        })}
                    </div>
                </section>

                {/* Default Voices */}
                <section className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Default Patient Voice</label>
                        <div className="flex gap-2">
                            <select
                                className="block w-full border rounded-md p-2"
                                value={config.patient_voice_id || ''}
                                onChange={e => setConfig({ ...config, patient_voice_id: e.target.value })}
                            >
                                <option value="">Select a default voice...</option>
                                {voices.map(v => (
                                    <option key={v.id || v.voiceURI} value={v.id || v.voiceURI}>
                                        {v.name} ({v.gender || 'neutral'})
                                    </option>
                                ))}
                            </select>
                            <button
                                onClick={() => handleTestVoice(config.patient_voice_id)}
                                className="bg-gray-100 p-2 rounded hover:bg-gray-200"
                                title="Test Voice"
                            >
                                ▶️
                            </button>
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Default Narrator Voice</label>
                        <div className="flex gap-2">
                            <select
                                className="block w-full border rounded-md p-2"
                                value={config.narrator_voice_id || ''}
                                onChange={e => setConfig({ ...config, narrator_voice_id: e.target.value })}
                            >
                                <option value="">Select a default voice...</option>
                                {voices.map(v => (
                                    <option key={v.id || v.voiceURI} value={v.id || v.voiceURI}>
                                        {v.name} ({v.gender || 'neutral'})
                                    </option>
                                ))}
                            </select>
                            <button
                                onClick={() => handleTestVoice(config.narrator_voice_id)}
                                className="bg-gray-100 p-2 rounded hover:bg-gray-200"
                                title="Test Voice"
                            >
                                ▶️
                            </button>
                        </div>
                    </div>
                </section>

                {/* Other Settings */}
                <section className="space-y-4 pt-4 border-t">
                    <div className="flex items-center gap-4">
                        <label className="block text-sm font-medium text-gray-700">Default Playback Rate</label>
                        <input
                            type="range" min="0.5" max="2.0" step="0.1"
                            value={config.default_rate}
                            onChange={e => setConfig({ ...config, default_rate: parseFloat(e.target.value) })}
                            className="w-48"
                        />
                        <span className="text-sm font-mono">{config.default_rate.toFixed(1)}x</span>
                    </div>

                    <div className="flex items-center gap-2">
                        <input
                            type="checkbox"
                            id="auto_gender"
                            checked={config.auto_gender_select}
                            onChange={e => setConfig({ ...config, auto_gender_select: e.target.checked })}
                            className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                        />
                        <label htmlFor="auto_gender" className="text-sm text-gray-700">
                            Auto-select gender appropriate voices based on patient profile
                        </label>
                    </div>

                    <div className="pt-4 border-t">
                        <label className="block text-sm font-medium text-gray-700 mb-3">Default Narration Mode</label>
                        <div className="flex flex-wrap gap-4">
                            {[
                                { id: 'off', label: 'Off', desc: 'No narration by default' },
                                { id: 'patient', label: 'Patient only', desc: 'Only patient speech is narrated' },
                                { id: 'all', label: 'Patient + Narrator', desc: 'Both speech and non-verbal actions (e.g. [Patient winces])' }
                            ].map(opt => (
                                <label
                                    key={opt.id}
                                    className={`flex-1 min-w-[200px] p-3 border rounded-lg cursor-pointer transition-colors ${config.default_narration === opt.id ? 'border-blue-500 bg-blue-50' : 'hover:bg-gray-50'}`}
                                >
                                    <div className="flex items-center mb-1">
                                        <input
                                            type="radio"
                                            name="default_narration"
                                            value={opt.id}
                                            checked={config.default_narration === opt.id}
                                            onChange={() => setConfig({ ...config, default_narration: opt.id })}
                                            className="mr-2"
                                        />
                                        <span className="font-medium text-sm">{opt.label}</span>
                                    </div>
                                    <p className="text-[11px] text-gray-500">{opt.desc}</p>
                                </label>
                            ))}
                        </div>
                    </div>
                </section>

                <div className="pt-6">
                    <button
                        onClick={handleSave}
                        disabled={saving}
                        className={`w-full py-3 px-4 rounded-md text-white font-bold transition-colors ${saving ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'}`}
                    >
                        {saving ? 'Saving...' : 'Save TTS Configuration'}
                    </button>
                </div>
            </div>

            <div className="mt-8 p-4 bg-amber-50 rounded-lg border border-amber-200">
                <h3 className="text-amber-800 font-bold flex items-center gap-2">
                    <span>⚠️</span> Note on Engines
                </h3>
                <ul className="text-sm text-amber-700 list-disc ml-5 mt-2 space-y-1">
                    <li><strong>Browser:</strong> Reliability varies by OS/Browser. User must interact with page once to enable speech.</li>
                    <li><strong>Edge TTS:</strong> Uses Microsoft's online neural voices. Extremely high quality and requires no local resources.</li>
                    <li><strong>Piper:</strong> Runs locally on the server (low latency). Requires the Piper container to be running at the configured URL.</li>
                </ul>
            </div>
        </div>
    );
};

export default TTSAdminPage;
